import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Divider, Grid, IconButton, InputLabel, Modal, Stack, Typography, Button } from '@mui/material';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import { Icon } from '@iconify/react';
import img1 from '../../assets/images/image1.png';
import QuickGlanceLoader from '../VehicleTabs/TabLoader';
import useMetaData from '../../hooks/useMetaData';
import { META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import { getVehicle, getVehicleConfiguration, populatePayload, simulator } from '../../services/vehicles';
import { jsonPathToValue } from '../../utils/json';
import applyType from '../../utils/metadataUtils';
import { generateRandomString } from '../../utils/string';
import GridLabels from '../VehicleVerticalTabs/GridLabels';
import { ReactComponent as Car } from './car.svg';
import { getVehicleOrder } from '../../services/order';
import { useAppDispatch } from '../../hooks/useRedux';
import { setCurrentStage, setSelectedStage, setVehicle } from '../../redux/slices/vehicle';
import { TextField, ModalBody } from '../../components';

type Props = {
  show: boolean;
  onHide: () => void;
  showToast: () => void;
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

type FormValues = {
  VIN: string;
  sapOrderNumber: string;
  salesforce: string;
  production: string;
  sequence: string;
  actual_factory_gate_date: string;
  ga_start_date: string;
  ga_off_final_date: string;
  planned_broadcast_date: string;
  orderId: string;
  VINGeneratedOn: string;
  VMSVehicleID: string;
  status: string;
  createdBy: string;
  updatedBy: string;
  vin_generation_date: string;
  SequenceNumber: string;
  order: string;
};

const ProductionModal = ({ show, onHide, showToast }: Props) => {
  // use hooks
  const id = useId();
  const dispatch = useAppDispatch();
  const { reservationId } = useParams<{ reservationId: string }>();
  const quickGlanceMetaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.vehicleTab);

  // local states
  const [configData, setConfigData] = useState<any>();
  const [currentStage, setLocalCurrentStage] = useState('scheduleForManufacturing');
  const [formValues, setFormValues] = useState<FormValues>({
    VIN: '',
    sapOrderNumber: '',
    salesforce: '',
    production: '',
    sequence: '',
    actual_factory_gate_date: '',
    ga_start_date: '',
    ga_off_final_date: '',
    planned_broadcast_date: '',
    orderId: '1234',
    VINGeneratedOn: '',
    VMSVehicleID: '',
    status: 'Schedule For Manufacturing',
    createdBy: '',
    updatedBy: '',
    vin_generation_date: '',
    SequenceNumber: '',
    order: '',
  });
  const [simulatorData, setStimulatorData] = useState<any>(null);
  const [orderData, setOrderData] = useState<any>(null);

  console.log(formValues, 'formValues');

  // constants
  const quickGlanceConfigLabels = useMemo(
    () =>
      configData
        ? quickGlanceMetaData?.rows?.map((row: any) => {
            const value = jsonPathToValue(configData, row.property);
            const isVIN = row.propertyName === 'VIN';

            if (isVIN) {
              return {
                label: row.propertyName,
                title: value || 'Not Assigned',
                id: generateRandomString(10),
              };
            }

            return {
              label: row?.propertyName,
              title: applyType(row.type, value),
              id: generateRandomString(10),
            };
          }) || []
        : [],
    [configData, quickGlanceMetaData?.rows]
  );

  const branchWidth = 400;
  const initialStepWidth = 35;
  const carIconHeight = 8;
  const curveHeight = 50;

  const stepPositions: Record<string, any> = {
    scheduleForManufacturing: {
      right: -initialStepWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    broadcastInQueue: {
      right: -initialStepWidth - (1 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    inBuild: {
      right: -initialStepWidth - (2 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    offBuild: {
      right: -initialStepWidth - (3 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    factoryGate: {
      right: -initialStepWidth - (4 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    outToTransit: {
      right: -initialStepWidth - (5 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
  };

  const allSteps = [
    'scheduleForManufacturing',
    'broadcastInQueue',
    'inBuild',
    'offBuild',
    'factoryGate',
    'outToTransit',
  ];

  const stagePriority: any = {
    scheduleForManufacturing: 1,
    broadcastInQueue: 2,
    inBuild: 3,
    offBuild: 4,
    factoryGate: 5,
    outToTransit: 6,
  };

  const stepDotPositions: Record<string, any> = {
    scheduleForManufacturing: {
      right: -initialStepWidth,
      top: -curveHeight,
      transform: `translate(50%)`,
    },
    broadcastInQueue: {
      right: -initialStepWidth - (1 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%)`,
    },
    inBuild: {
      right: -initialStepWidth - (2 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%)`,
    },
    offBuild: {
      right: -initialStepWidth - (3 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%)`,
    },
    factoryGate: {
      right: -initialStepWidth - (4 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(15%)`,
    },
    outToTransit: {
      right: -initialStepWidth - (5 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%)`,
    },
  };

  const texts: Record<string, string> = {
    scheduleForManufacturing: 'Schedule For Manufacturing',
    broadcastInQueue: 'Broadcast In Queue',
    inBuild: 'In Build',
    offBuild: 'Off Build',
    factoryGate: 'Factory Gate',
    outToTransit: 'Out To Transit',
  };

  const statuses: any = {
    scheduleForManufacturing: 'Schedule For Manufacturing',
    broadcastInQueue: 'BROADCAST_QUEUE',
    inBuild: 'IN_BUILD',
    offBuild: 'OFF_FINAL',
    factoryGate: 'FACTORY_GATE',
    outToTransit: 'Out To Transit',
  };

  const reverseStatuses: any = useMemo(() => {
    const data = {
      'Schedule For Manufacturing': 'scheduleForManufacturing',
      BROADCAST_QUEUE: 'broadcastInQueue',
      IN_BUILD: 'inBuild',
      OFF_FINAL: 'offBuild',
      FACTORY_GATE: 'factoryGate',
      'Out To Transit': 'outToTransit',
    };
    return data;
  }, []);

  // handling form values
  const handleValues = (e: any, type?: string) => {
    const { name, value } = e.target;

    setFormValues((prev) => {
      return { ...prev, [name]: type === 'date' ? moment(value, 'Y-MM-DD')?.format() : value };
    });
  };

  const handleDateValue = (value: string) => {
    return moment(value)?.format('Y-MM-DD');
  };

  // handling preprocess
  const onPreprocess = async (formData: any) => {
    try {
      await populatePayload({
        method: 'POST',
        url: 'populatepayload',
        data: { orderId: formData?.orderId },
      });
      onHide();
    } catch (error) {
      console.log(error);
    }
  };

  // fetching simulator record
  const onSubmit = useCallback(
    async (formData: any) => {
      Object.keys(formData)?.map((item: any) => {
        if (!formData[item] || formData[item] === '') delete formData[item];
        return formData;
      });
      try {
        const resp = await simulator({ method: 'POST', url: 'saveDetail', data: { ...formData, reservationId } });
        const resp2 = await populatePayload({
          method: 'POST',
          url: 'populatepayload',
          data: { orderId: formData?.orderId },
        });
      } catch (error) {
        console.log(error);
      }
      if (reservationId)
        getVehicle(reservationId).then((vehicle) => {
          dispatch(setVehicle(vehicle));
          dispatch(setCurrentStage(vehicle?.currentStage)); // setting current stage
          dispatch(setSelectedStage(vehicle?.currentStage)); // setting selected stage
          showToast();
          onHide();
        });
    },
    [reservationId, dispatch, onHide, showToast]
  );

  // lifecycle hooks
  useEffect(() => {
    if (reservationId) {
      getVehicleConfiguration(reservationId).then((response) => setConfigData(response));
      // simulator({ method: 'GET', url: 'getOrders', params: { reservationId, stage: 'Ordered' } }).then((resp) => {
      //   setStimulatorData(resp?.data?.data);
      // });
    }
  }, [reservationId]);

  useEffect(() => {
    if (reservationId) {
      getVehicleOrder(reservationId).then((response) => {
        setFormValues((prev) => {
          return {
            ...prev,
            orderId: response?.orderId && response?.orderId !== '' ? response?.orderId : generateRandomString(15),
          };
        });
        if (response?.orderId)
          populatePayload({ method: 'GET', url: response?.orderId }).then((resp) => {
            setFormValues((prev) => {
              return {
                ...prev,
                status: resp?.data?.data?.status,
                // VINGeneratedOn: resp?.data?.data?.VINGeneratedOn
                //   ? moment(resp?.data?.data?.VINGeneratedOn)?.format()
                //   : '',
                vin_generation_date: resp?.data?.data?.vin_generation_date
                  ? moment(resp?.data?.data?.vin_generation_date)?.format()
                  : '',
                VIN: resp?.data?.data?.VIN,
                VMSVehicleID: resp.data?.data?.VMSVehicleID,
                createdBy: resp.data?.data?.createdBy,
                updatedBy: resp.data?.data?.updatedBy,
                planned_broadcast_date: resp.data?.data?.planned_broadcast_date
                  ? moment(resp.data?.data?.planned_broadcast_date)?.format()
                  : '',
                ga_start_date: resp.data?.data?.ga_start_date ? moment(resp.data?.data?.ga_start_date)?.format() : '',
                ga_off_final_date: resp.data?.data?.ga_off_final_date
                  ? moment(resp.data?.data?.ga_off_final_date)?.format()
                  : '',
                actual_factory_gate_date: resp.data?.data?.actual_factory_gate_date
                  ? moment(resp.data?.data?.actual_factory_gate_date)?.format()
                  : '',
                SequenceNumber: resp.data?.data?.SequenceNumber,
                order: resp.data?.data?.order,
              };
            });
            setLocalCurrentStage(reverseStatuses[resp?.data?.data?.status]);
          });
      });
    }
  }, [reservationId, reverseStatuses]);

  if (!quickGlanceMetaData || !configData) return <QuickGlanceLoader />;
  return (
    <Modal open={show} onClose={onHide} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <ModalBody>
        <Box display="flex" alignItems="center" sx={{ marginBottom: '35px' }}>
          <Box flexGrow={1}>
            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ fontSize: '24px', fontWeight: '500' }}>
              Production Manufacturing Simulator
            </Typography>
          </Box>

          <IconButton onClick={() => onHide()} sx={{ marginRight: '-10px' }}>
            <Icon icon="ei:close" />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item sm={12} md={6} lg={4}>
            <Box sx={{ width: '100%', background: '#F5F5F5' }}>
              <img src={img1} alt="car" style={{ width: '100%', minHeight: 120 }} />
              <Divider sx={{ mt: 2, borderColor: '#E9E9E9;' }} />

              <Box sx={{ width: '100%' }}>
                <Grid container spacing={2} mt={0} sx={{ padding: '0px 20px 20px 20px' }}>
                  <GridLabels labels={quickGlanceConfigLabels} />
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item sm={12} md={6} lg={8}>
            <Box sx={{ paddingBottom: '15px', paddingLeft: '5px' }}>
              <Button
                variant="contained"
                onClick={() => onPreprocess(formValues)}
                sx={{ textTransform: 'none', fontSize: '14px', fontWeight: 400, boxShadow: 'none' }}
              >
                Preprocess
              </Button>
            </Box>
            <Box bgcolor="common.white" sx={{ padding: '0px 0px 0px 20px' }}>
              <Grid container spacing={2} sx={{ background: '#F5F5F5', padding: '0px 20px 20px 4px', marginTop: '0' }}>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    VIN
                  </InputLabel>
                  <TextField
                    type="text"
                    name="VIN"
                    value={formValues?.VIN}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter VIN Number"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                {/* <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    VIN Generated On
                  </InputLabel>
                  <TextField
                    type="date"
                    name="VINGeneratedOn"
                    value={handleDateValue(formValues?.VINGeneratedOn)}
                    onChange={(e) => handleValues(e, 'date')}
                    id={id}
                    placeholder="dd/mm/yyyy"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid> */}
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    VIN Generation Date
                  </InputLabel>
                  <TextField
                    type="date"
                    name="vin_generation_date"
                    value={handleDateValue(formValues?.vin_generation_date)}
                    onChange={(e) => handleValues(e, 'date')}
                    id={id}
                    placeholder="dd/mm/yyyy"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    VMS Vehicle ID
                  </InputLabel>
                  <TextField
                    type="text"
                    name="VMSVehicleID"
                    value={formValues?.VMSVehicleID}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter VMS Vehicle ID"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Status
                  </InputLabel>
                  <TextField
                    type="text"
                    name="status"
                    value={formValues?.status}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter status"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Created By
                  </InputLabel>
                  <TextField
                    type="text"
                    name="createdBy"
                    value={formValues?.createdBy}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Created By"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Updated By
                  </InputLabel>
                  <TextField
                    type="text"
                    name="updatedBy"
                    value={formValues?.updatedBy}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Updated By"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Order Id (Customer Reference Id)
                  </InputLabel>
                  <TextField
                    type="text"
                    name="orderId"
                    value={formValues?.orderId}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Order ID"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Production Order
                  </InputLabel>
                  <TextField
                    type="text"
                    name="order"
                    value={formValues?.order}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Order ID"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Sequence Number
                  </InputLabel>
                  <TextField
                    type="text"
                    name="SequenceNumber"
                    value={formValues?.SequenceNumber}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Sequence Number"
                    sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                  />
                </Grid>
                {/* <Grid item xs={4}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    SAP Order Number
                  </InputLabel>
                  <TextField
                    type="text"
                    name="sapOrderNumber"
                    value={formValues?.sapOrderNumber}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter SAO Order Number"
                    sx={{ fontSize: '12px', height: '36px', background: '#fff', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Salesforce Order Number
                  </InputLabel>
                  <TextField
                    type="text"
                    name="salesforce"
                    value={formValues?.salesforce}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Salesforce Order Number"
                    sx={{ fontSize: '12px', height: '36px', background: '#fff', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Production Order Number
                  </InputLabel>
                  <TextField
                    type="text"
                    name="production"
                    value={formValues?.production}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Product Order Number"
                    sx={{ fontSize: '12px', height: '36px', background: '#fff', borderRadius: '4px' }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <InputLabel
                    htmlFor={id}
                    sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                  >
                    Sequence Number
                  </InputLabel>
                  <TextField
                    type="text"
                    name="sequence"
                    value={formValues?.sequence}
                    onChange={(e) => handleValues(e)}
                    id={id}
                    placeholder="Enter Esquence Number"
                    sx={{ fontSize: '12px', height: '36px', background: '#fff', borderRadius: '4px' }}
                  />
                </Grid> */}
              </Grid>
            </Box>

            <Box bgcolor="common.white" sx={{ padding: '23px 0px 0px 0px' }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontSize: '20px', fontWeight: '500', color: '#1C1951', textAlign: 'center' }}
              >
                Production Lifecycle
              </Typography>

              {/*  - - - - - - - - - - */}

              <Box width="100%">
                <Stack sx={{ width: '100%', position: 'relative', paddingTop: '72px', paddingBottom: '16px' }}>
                  {/*
                   *  Initial steps
                   */}
                  <Box height={4} width={initialStepWidth} position="relative">
                    {/**
                     * Moving user icon
                     */}
                    <Box position="absolute" sx={{ ...(stepPositions[currentStage] || {}), zIndex: 4 }}>
                      <Car />
                    </Box>

                    {/**
                     * step labels
                     */}
                    {allSteps.map((step) => (
                      <Box
                        position="absolute"
                        sx={{ ...(stepDotPositions[step] || {}), zIndex: 2, cursor: 'pointer' }}
                        height={14}
                        width={14}
                        bgcolor="#fff"
                        borderRadius="50%"
                        marginTop="-3px"
                        border="1px solid #C5C5C5"
                        onClick={() => {
                          setFormValues((prev) => {
                            return { ...prev, status: statuses[step] };
                          });
                          setLocalCurrentStage(step);
                        }}
                      >
                        <Box position="relative">
                          <Typography
                            position="absolute"
                            width={130}
                            sx={{ translate: '-45%' }}
                            textAlign="center"
                            top={23}
                            fontSize={14}
                            fontWeight={500}
                            color="#000"
                            lineHeight={1.2}
                          >
                            {texts[step]}
                          </Typography>
                        </Box>
                      </Box>
                    ))}

                    {/**
                     * Vehicle stage branch
                     */}
                    <Box
                      bgcolor={stagePriority[currentStage] > 1 ? '#000' : '#000'}
                      height="1px"
                      width={branchWidth / 3}
                      position="absolute"
                      right={-((1 / 3) * branchWidth) - 35}
                      top={-curveHeight + 4}
                    />
                    <Box
                      bgcolor={stagePriority[currentStage] > 2 ? '#000' : '#000'}
                      height="1px"
                      width={branchWidth / 3}
                      position="absolute"
                      right={-((2 / 3) * branchWidth) - 35}
                      top={-curveHeight + 4}
                    />
                    <Box
                      bgcolor={stagePriority[currentStage] > 3 ? '#000' : '#000'}
                      height="1px"
                      width={branchWidth / 3}
                      position="absolute"
                      right={-branchWidth - 35}
                      top={-curveHeight + 4}
                    />
                    <Box
                      bgcolor={stagePriority[currentStage] > 4 ? '#000' : '#000'}
                      height="1px"
                      width={branchWidth / 3}
                      position="absolute"
                      right={-((4 / 3) * branchWidth) - 35}
                      top={-curveHeight + 4}
                    />
                    <Box
                      bgcolor={stagePriority[currentStage] > 5 ? '#000' : '#000'}
                      height="1px"
                      width={branchWidth / 3}
                      position="absolute"
                      right={-((5 / 3) * branchWidth) - 35}
                      top={-curveHeight + 4}
                    />
                  </Box>
                </Stack>
              </Box>
              {/*  - - - - - - - - - - */}
              <Box
                sx={{
                  height: '1px',
                  borderTop: '1px solid #e3e3e3',
                  width: 'calc(100% - 25px)',
                  margin: 'auto',
                  marginLeft: '25px',
                }}
              />
              <Box bgcolor="common.white" sx={{ padding: '20px 0px 0px 0px', maxWidth: '415px', margin: 'auto' }}>
                <Grid container spacing={2} sx={{ padding: '0px 0px 0px 4px' }}>
                  {(formValues?.status === 'FACTORY_GATE' || formValues?.status === 'Schedule For Manufacturing') && (
                    <Grid item xs={6}>
                      <InputLabel
                        htmlFor={id}
                        sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                      >
                        Actual Factory Gate Date
                      </InputLabel>
                      <TextField
                        type="date"
                        name="actual_factory_gate_date"
                        value={handleDateValue(formValues.actual_factory_gate_date)}
                        onChange={(e) => handleValues(e, 'date')}
                        id={id}
                        placeholder="dd/mm/yyyy"
                        sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                      />
                    </Grid>
                  )}
                  {(formValues?.status === 'BROADCAST_QUEUE' ||
                    formValues?.status === 'Schedule For Manufacturing') && (
                    <Grid item xs={6}>
                      <InputLabel
                        htmlFor={id}
                        sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                      >
                        Planned Broadcast Date
                      </InputLabel>
                      <TextField
                        type="date"
                        name="planned_broadcast_date"
                        value={handleDateValue(formValues?.planned_broadcast_date)}
                        onChange={(e) => handleValues(e, 'date')}
                        id={id}
                        placeholder="dd/mm/yyyy"
                        sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                      />
                    </Grid>
                  )}
                  {(formValues?.status === 'IN_BUILD' || formValues?.status === 'Schedule For Manufacturing') && (
                    <Grid item xs={6}>
                      <InputLabel
                        htmlFor={id}
                        sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                      >
                        GA Start Date
                      </InputLabel>
                      <TextField
                        type="date"
                        name="ga_start_date"
                        value={handleDateValue(formValues?.ga_start_date)}
                        onChange={(e) => handleValues(e, 'date')}
                        id={id}
                        placeholder="dd/mm/yyyy"
                        sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                      />
                    </Grid>
                  )}
                  {(formValues?.status === 'OFF_FINAL' || formValues?.status === 'Schedule For Manufacturing') && (
                    <Grid item xs={6}>
                      <InputLabel
                        htmlFor={id}
                        sx={{ fontSize: '12px', fontWeight: '400', color: '#000', paddingBottom: '4px' }}
                      >
                        GA Off Final Date
                      </InputLabel>
                      <TextField
                        type="date"
                        name="ga_off_final_date"
                        value={handleDateValue(formValues?.ga_off_final_date)}
                        onChange={(e) => handleValues(e, 'date')}
                        id={id}
                        placeholder="dd/mm/yyyy"
                        sx={{ fontSize: '12px', height: '36px', borderRadius: '4px' }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Divider sx={{ borderColor: '#e3e3e3', margin: '15px 0 15px 25px' }} />
              <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  onClick={() => onSubmit(formValues)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    boxShadow: 'none',
                  }}
                >
                  Move Vehicle
                </Button>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </ModalBody>
    </Modal>
  );
};

export default ProductionModal;
